import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Drumstick, Beef, Sandwich, Fish, ChefHat, Salad,
  IceCream, GlassWater, Utensils, Plus, ChevronDown,
  ArrowRight, Star, Flame, X,
} from "lucide-react";
import { fetchMenu, resolveImageUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

// Custom SVG icon for nacho/chip (no good lucide match)
const NachoIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M3 6 L21 6 L12 21 Z" />
    <path d="M7 10 L17 10" />
    <path d="M9 14 L15 14" />
  </svg>
);
const PastaIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M4 12 C 4 7, 8 4, 12 4 C 16 4, 20 7, 20 12" />
    <path d="M4 12 L4 18 C 4 19, 5 20, 6 20 L 18 20 C 19 20, 20 19, 20 18 L 20 12" />
    <path d="M8 13 L8 19" />
    <path d="M12 13 L12 19" />
    <path d="M16 13 L16 19" />
  </svg>
);
const FriesIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M6 8 L18 8 L17 21 L7 21 Z" />
    <path d="M8 8 L8 4 M11 8 L11 3 M14 8 L14 4 M17 8 L17 5" />
  </svg>
);

const CATEGORY_META = {
  wings:        { label: "Wings",          Icon: Drumstick,  blurb: "31 flavors · jumbo cuts" },
  burgers:      { label: "Burgers",        Icon: Beef,       blurb: "Burgers with attitude · handmade on deluxe bun" },
  philly:       { label: "Philly",         Icon: Sandwich,   blurb: "6-inch and 12-inch hoagies" },
  seafood:      { label: "Seafood",        Icon: Fish,       blurb: "On The Bluff — fried fresh" },
  nachos:       { label: "Nachos",         Icon: NachoIcon,  blurb: "Lock'd & loaded with meats" },
  appetizers:   { label: "Finger Foods",   Icon: ChefHat,    blurb: "Tenders, dogs, mozz sticks" },
  pasta:        { label: "Pasta",          Icon: PastaIcon,  blurb: "Creamy Alfredo · Thursdays & Fridays only" },
  quesadillas:  { label: "Quesadillas",    Icon: Utensils,   blurb: "Oowey Gooey · 4 cheeses" },
  salads:       { label: "Salads & Wraps", Icon: Salad,      blurb: "Cobb, Caesar, garden + 3 wraps" },
  sides:        { label: "Fries & Sides",  Icon: FriesIcon,  blurb: "Junkyard, loaded, chili cheese" },
  desserts:     { label: "Desserts",       Icon: IceCream,   blurb: "Banana pudding · made fresh" },
  drinks:       { label: "Drinks",         Icon: GlassWater, blurb: "Pepsi fountain, bottled, can" },
};

const CATEGORY_ORDER = [
  "wings", "burgers", "philly", "seafood", "nachos",
  "appetizers", "pasta", "quesadillas", "salads", "sides", "desserts", "drinks",
];

export const HomeMenuAccordion = () => {
  const [items, setItems] = useState([]);
  const [openCat, setOpenCat] = useState("wings");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const { addItem, setTaxRate } = useCart();

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchMenu();
        setItems(data.items || []);
        if (data.tax_rate) setTaxRate(data.tax_rate);
      } catch {
        toast.error("Couldn't load menu.");
      } finally {
        setLoading(false);
      }
    })();
  }, [setTaxRate]);

  const handleAdd = (item, e) => {
    e?.stopPropagation();
    toast.info("Food orders go through DoorDash", {
      description: "Tap the red 'Order on DoorDash' floating button at the bottom of the page.",
      duration: 4500,
    });
  };

  return (
    <section data-testid="home-menu-accordion" className="bg-[#0A0A0A] py-20 md:py-28 border-t border-[#222]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10 md:mb-12 text-center">
          <div className="text-xs uppercase tracking-widest text-[#E60000] font-bold mb-3">Browse The Menu</div>
          <h2 className="font-heading text-5xl sm:text-6xl text-white mb-4">
            Tap. Open. <span className="text-[#E60000]">Order.</span>
          </h2>
          <p className="text-white/60 max-w-xl mx-auto">
            12 categories. 69 dishes. Tap a category to see what's in it.
          </p>
        </div>

        {loading ? (
          <div className="text-center text-white/50 uppercase tracking-widest py-16">Loading menu…</div>
        ) : (
          <ul className="border-2 border-[#222] bg-[#141414] divide-y divide-[#222]">
            {CATEGORY_ORDER.map((cat) => {
              const meta = CATEGORY_META[cat];
              const catItems = items.filter((i) => i.category === cat);
              if (catItems.length === 0) return null;
              const isOpen = openCat === cat;
              const Icon = meta.Icon;
              return (
                <li key={cat} data-testid={`accordion-${cat}`}>
                  <button
                    data-testid={`accordion-toggle-${cat}`}
                    onClick={() => setOpenCat(isOpen ? null : cat)}
                    className={`w-full flex items-center gap-4 px-5 md:px-7 py-5 text-left transition-colors ${
                      isOpen ? "bg-[#1F1F1F]" : "hover:bg-[#1A1A1A]"
                    }`}
                  >
                    <div className={`w-12 h-12 flex items-center justify-center border-2 transition-colors ${
                      isOpen ? "bg-[#E60000] border-[#E60000] text-white" : "border-[#333] text-[#E60000]"
                    }`}>
                      <Icon className="w-6 h-6" strokeWidth={2.2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-heading text-2xl md:text-3xl text-white leading-tight">{meta.label}</h3>
                        <span className="text-[10px] uppercase tracking-widest text-white/40 font-bold bg-black px-2 py-0.5 border border-[#333]">
                          {catItems.length} {catItems.length === 1 ? "item" : "items"}
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm text-white/50 mt-1">{meta.blurb}</p>
                    </div>
                    <ChevronDown
                      className={`w-5 h-5 text-white/60 flex-shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180 text-[#E60000]" : ""}`}
                      strokeWidth={2.5}
                    />
                  </button>

                  <div
                    className={`grid transition-all duration-300 ease-out ${
                      isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="px-5 md:px-7 pb-6 pt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {catItems.map((item) => (
                          <div
                            key={item.id}
                            data-testid={`accordion-item-${item.id}`}
                            onClick={() => setDetail(item)}
                            className="group flex items-start gap-3 p-3 border border-[#222] hover:border-[#E60000] bg-[#0A0A0A] transition-colors cursor-pointer"
                          >
                            {item.image_url && (
                              <img
                                src={resolveImageUrl(item.image_url)}
                                alt={item.name}
                                className="w-14 h-14 object-cover flex-shrink-0"
                                loading="lazy"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1 flex-wrap mb-0.5">
                                <h4 className="font-heading text-base text-white leading-tight">{item.name}</h4>
                                {item.popular && <Star className="w-3 h-3 fill-[#E60000] text-[#E60000]" strokeWidth={0} />}
                                {item.spicy && <Flame className="w-3 h-3 text-[#E60000]" strokeWidth={2.5} />}
                              </div>
                              <div className="text-[#E60000] font-bold text-sm">${item.price.toFixed(2)}</div>
                            </div>
                            <button
                              data-testid={`accordion-expand-${item.id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDetail(item);
                              }}
                              className="self-center w-9 h-9 flex-shrink-0 flex items-center justify-center bg-[#1A1A1A] border border-[#333] hover:bg-[#E60000] hover:border-[#E60000] text-white transition-colors"
                              aria-label={`View details for ${item.name}`}
                            >
                              <Plus className="w-4 h-4" strokeWidth={3} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <div className="mt-8 text-center">
          <Link
            data-testid="home-full-menu-cta"
            to="/menu"
            className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF3333] text-white font-bold uppercase tracking-widest text-sm px-6 h-12 border-2 border-transparent hover:border-white transition-colors"
          >
            View Full Menu <ArrowRight className="w-4 h-4" strokeWidth={3} />
          </Link>
        </div>
      </div>

      {detail && (
        <div
          data-testid="item-modal"
          onClick={() => setDetail(null)}
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 cursor-zoom-out"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-[#141414] border-2 border-[#E60000] max-w-4xl w-full max-h-[92vh] overflow-y-auto cursor-default"
          >
            <div className="relative grid md:grid-cols-2">
              <button
                data-testid="modal-close"
                onClick={() => setDetail(null)}
                className="absolute top-3 right-3 z-10 w-10 h-10 bg-black/90 hover:bg-[#E60000] text-white flex items-center justify-center border-2 border-white"
              >
                <X className="w-5 h-5" strokeWidth={2.5} />
              </button>
              {detail.image_url && (
                <div className="aspect-square bg-black">
                  <img src={resolveImageUrl(detail.image_url)} alt={detail.name} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-6 md:p-8 flex flex-col">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  {detail.popular && (
                    <span className="inline-flex items-center gap-1 bg-[#E60000] text-white text-[10px] uppercase tracking-widest font-bold px-2 py-0.5">
                      <Star className="w-3 h-3" strokeWidth={3} /> Popular
                    </span>
                  )}
                  {detail.spicy && (
                    <span className="inline-flex items-center gap-1 bg-black/80 text-white text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border border-[#E60000]">
                      <Flame className="w-3 h-3 text-[#E60000]" strokeWidth={3} /> Spicy
                    </span>
                  )}
                </div>
                <h3 className="font-heading text-4xl md:text-5xl text-white mb-3">{detail.name}</h3>
                <div className="text-[#E60000] font-heading text-3xl mb-5">${detail.price.toFixed(2)}</div>

                <div className="text-xs uppercase tracking-widest text-white/60 font-bold mb-2">What's In It</div>
                <p className="text-white/85 leading-relaxed text-base mb-6">{detail.description}</p>

                <div className="mt-auto border-t border-[#222] pt-5 space-y-2">
                  <div className="text-xs uppercase tracking-widest text-[#E60000] font-bold mb-2">Ready to order?</div>
                  <p className="text-sm text-white/70 mb-3">
                    Food orders go through DoorDash. Tap the floating <span className="text-[#E60000] font-bold">Order on DoorDash</span> button at the bottom of the page.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
